import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from 'react';

interface LoadingContextValue {
  isLoading: boolean;
  startLoading: () => void;
  stopLoading: () => void;
}

const LoadingContext = createContext<LoadingContextValue | null>(null);

export function LoadingProvider({ children }: { children: ReactNode }) {
  const pendingRef = useRef(0);
  const [isLoading, setIsLoading] = useState(false);

  const startLoading = useCallback(() => {
    pendingRef.current += 1;
    if (pendingRef.current === 1) {
      setIsLoading(true);
    }
  }, []);

  const stopLoading = useCallback(() => {
    pendingRef.current = Math.max(0, pendingRef.current - 1);
    if (pendingRef.current === 0) {
      setIsLoading(false);
    }
  }, []);

  return (
    <LoadingContext.Provider value={{ isLoading, startLoading, stopLoading }}>
      {children}
      {isLoading && (
        <div
          className="fixed inset-0 z-[95] flex items-center justify-center bg-slate-900/25 backdrop-blur-[3px]"
          aria-live="polite"
          aria-busy="true"
          role="status"
        >
          <div className="flex flex-col items-center gap-4 rounded-2xl bg-white/95 px-10 py-8 shadow-2xl shadow-teal-900/10 border border-teal-100/80 animate-fadeIn">
            <div className="relative w-16 h-16">
              <div className="absolute inset-0 rounded-2xl border-[3px] border-teal-100" />
              <div className="absolute inset-0 rounded-2xl border-[3px] border-transparent border-t-teal-500 animate-spin" />
              <div className="absolute inset-[6px] rounded-xl bg-teal-500 flex items-center justify-center text-white font-black text-xl shadow-md shadow-teal-500/30">
                O
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-600 tracking-wide">
              <i className="ri-loader-4-line text-teal-500 text-base animate-spin" />
              <span>Envoi en cours…</span>
            </div>
          </div>
        </div>
      )}
    </LoadingContext.Provider>
  );
}

export function useGlobalLoading() {
  const ctx = useContext(LoadingContext);
  if (!ctx) {
    throw new Error('useGlobalLoading doit être utilisé dans LoadingProvider');
  }
  return ctx;
}
